import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Image as ImageIcon, Link as LinkIcon, DollarSign, Search, Trash2, Eye, EyeOff, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Tables } from "@/integrations/supabase/types";
import { ImageUpload, VehicleImages } from "@/components/admin/ImageUpload";
import { submitVehicleToIndexNow, submitInventoryToIndexNow } from "@/utils/indexNow";
import { logActivity } from "@/utils/customerSync";
import { logAudit, computeChanges } from "@/utils/auditLog";
import { validateVIN } from "@/utils/validation";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { useAuth } from "@/hooks/useAuth";

type Vehicle = Tables<"vehicles">;
type VehicleImage = Tables<"vehicle_images">;

export default function InventoryManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [vehicleImages, setVehicleImages] = useState<Record<string, VehicleImage[]>>({});
  const [formData, setFormData] = useState({
    make: "",
    model: "",
    year: new Date().getFullYear(),
    price: "",
    mileage: "",
    color: "",
    interior_color: "",
    vin: "",
    transmission: "automatic",
    fuel_type: "gasoline",
    drivetrain: "fwd",
    description: "",
    status: "available" as "available" | "sold" | "pending",
    featured: false,
    carfax_url: "",
    purchase_cost: "",
    purchase_hst: "",
    purchase_date: "",
    body_type: "",
    trim: "",
    stock_number: "",
  });

  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("vehicles")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load vehicles");
    } else {
      setVehicles(data || []);
      // Load images for all vehicles in a single batch query (replaces N+1 pattern)
      if (data && data.length > 0) {
        const vehicleIds = data.map((v) => v.id);
        const { data: allImages } = await supabase
          .from("vehicle_images")
          .select("*")
          .in("vehicle_id", vehicleIds)
          .order("display_order", { ascending: true });

        const imagesMap: Record<string, VehicleImage[]> = {};
        (allImages || []).forEach((img) => {
          if (!imagesMap[img.vehicle_id]) imagesMap[img.vehicle_id] = [];
          imagesMap[img.vehicle_id].push(img);
        });
        setVehicleImages(imagesMap);
      }
    }
    setIsLoading(false);
  };

  const loadVehicleImages = async (vehicleId: string) => {
    const { data } = await supabase
      .from("vehicle_images")
      .select("*")
      .eq("vehicle_id", vehicleId)
      .order("display_order", { ascending: true })
      .order("is_primary", { ascending: false });

    setVehicleImages(prev => ({
      ...prev,
      [vehicleId]: data || []
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving) return;

    if (!validateVIN(formData.vin)) {
      toast.error("Please enter a valid 17-character VIN (excluding letters I, O, Q)");
      return;
    }

    const priceVal = parseFloat(formData.price) || 0;
    const mileageVal = parseInt(formData.mileage) || 0;
    const purchaseCostVal = formData.purchase_cost ? parseFloat(formData.purchase_cost) : null;
    const purchaseHstVal = formData.purchase_hst ? parseFloat(formData.purchase_hst) : null;

    if (priceVal < 0 || mileageVal < 0 || (purchaseCostVal !== null && purchaseCostVal < 0) || (purchaseHstVal !== null && purchaseHstVal < 0)) {
      toast.error("Financial and quantity values cannot be negative");
      return;
    }

    setIsSaving(true);
    try {
      const vehicleData = {
        make: formData.make,
        model: formData.model,
        year: formData.year,
        price: priceVal,
        mileage: mileageVal,
        color: formData.color,
        interior_color: formData.interior_color || null,
        vin: formData.vin.trim().toUpperCase(),
        transmission: formData.transmission,
        fuel_type: formData.fuel_type,
        drivetrain: formData.drivetrain,
        description: formData.description,
        status: formData.status,
        featured: formData.featured,
        carfax_url: formData.carfax_url || null,
        purchase_cost: purchaseCostVal,
        purchase_hst: purchaseHstVal,
        purchase_date: formData.purchase_date || null,
        body_type: formData.body_type || null,
        trim: formData.trim || null,
        stock_number: formData.stock_number || null,
      };

      if (editingVehicle) {
        const { error } = await supabase
          .from("vehicles")
          .update(vehicleData)
          .eq("id", editingVehicle.id);

        if (error) {
          toast.error("Failed to update vehicle");
        } else {
          toast.success("Vehicle updated successfully");
          submitVehicleToIndexNow(editingVehicle.id);
          // Compute changes for audit log
          const changes = computeChanges(
            editingVehicle as unknown as Record<string, unknown>,
            vehicleData as unknown as Record<string, unknown>,
            ['make', 'model', 'year', 'price', 'mileage', 'color', 'interior_color', 'vin', 'transmission', 'fuel_type', 'drivetrain', 'status', 'featured', 'purchase_cost', 'purchase_hst', 'purchase_date', 'body_type', 'trim', 'stock_number']
          );
          await logAudit({
            entityType: 'vehicle',
            entityId: editingVehicle.id,
            action: 'updated',
            changes: Object.keys(changes).length > 0 ? changes : undefined,
            metadata: { name: `${formData.year} ${formData.make} ${formData.model}`, vin: formData.vin },
            performedBy: user?.id,
          });
          setIsDialogOpen(false);
          setEditingVehicle(null);
          resetForm();
          loadVehicles();
        }
      } else {
        const { data, error } = await supabase.from("vehicles").insert(vehicleData).select().single();

        if (error) {
          toast.error("Failed to add vehicle");
        } else {
          toast.success("Vehicle added! You can now add images.");
          if (data) {
            submitVehicleToIndexNow(data.id);
            submitInventoryToIndexNow();
            await logAudit({
              entityType: 'vehicle',
              entityId: data.id,
              action: 'created',
              metadata: { name: `${formData.year} ${formData.make} ${formData.model}`, vin: formData.vin },
              performedBy: user?.id,
            });
          }
          setEditingVehicle(data);
          loadVehicles();
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      make: "",
      model: "",
      year: new Date().getFullYear(),
      price: "",
      mileage: "",
      color: "",
      interior_color: "",
      vin: "",
      transmission: "automatic",
      fuel_type: "gasoline",
      drivetrain: "fwd",
      description: "",
      status: "available",
      featured: false,
      carfax_url: "",
      purchase_cost: "",
      purchase_hst: "",
      purchase_date: "",
      body_type: "",
      trim: "",
      stock_number: "",
    });
  };

  const openEditDialog = (vehicle: Vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      price: vehicle.price.toString(),
      mileage: vehicle.mileage.toString(),
      color: vehicle.color,
      interior_color: vehicle.interior_color || "",
      vin: vehicle.vin,
      transmission: vehicle.transmission,
      fuel_type: vehicle.fuel_type,
      drivetrain: vehicle.drivetrain || "fwd",
      description: vehicle.description || "",
      status: vehicle.status,
      featured: vehicle.featured,
      carfax_url: vehicle.carfax_url || "",
      purchase_cost: vehicle.purchase_cost ? vehicle.purchase_cost.toString() : "",
      purchase_hst: vehicle.purchase_hst ? vehicle.purchase_hst.toString() : "",
      purchase_date: vehicle.purchase_date || "",
      body_type: vehicle.body_type || "",
      trim: vehicle.trim || "",
      stock_number: vehicle.stock_number || "",
    });
    setIsDialogOpen(true);
  };

  const updateVehicleStatus = async (vehicleId: string, status: "available" | "sold" | "pending") => {
    if (status === "sold") {
      toast.error("Vehicles can only be marked as sold by creating a Deal.");
      return;
    }

    const { error } = await supabase
      .from("vehicles")
      .update({ 
        status,
        sold_price: null,
        sold_date: null,
        hst_collected: null,
        warranty_amount: null,
        warranty_name: null,
        trade_in_value: null,
      })
      .eq("id", vehicleId);

    if (error) {
      toast.error("Failed to update status");
    } else {
      toast.success("Status updated");
      submitVehicleToIndexNow(vehicleId);
      submitInventoryToIndexNow();
      loadVehicles();
    }
  };


  const generateLeadLink = (vehicleId: string) => {
    const url = `${window.location.origin}/inquire?vehicle_id=${vehicleId}&source=facebook`;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Lead capture link copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy link");
    });
  };

  const deleteVehicle = async (vehicleId: string) => {
    const { error } = await supabase.rpc('soft_delete_vehicle', {
      p_vehicle_id: vehicleId,
      p_deleted_by: user?.id || '',
    });

    if (error) {
      toast.error('Failed to delete vehicle: ' + error.message);
    } else {
      toast.success('Vehicle moved to recycle bin');
      submitInventoryToIndexNow();
      loadVehicles();
    }
    setDeleteTarget(null);
  };

  const toggleVehicleHidden = async (vehicleId: string, currentHidden: boolean) => {
    const { data, error } = await supabase
      .from("vehicles")
      .update({ is_hidden: !currentHidden })
      .eq("id", vehicleId)
      .select("is_hidden")
      .single();
    if (error || !data) {
      toast.error("Failed to update visibility — try refreshing the page");
    } else {
      toast.success(data.is_hidden ? "Vehicle hidden from website" : "Vehicle visible on website");
      await logAudit({
        entityType: 'vehicle',
        entityId: vehicleId,
        action: data.is_hidden ? 'hidden' : 'unhidden',
        changes: { is_hidden: { from: currentHidden, to: data.is_hidden } },
        metadata: { name: vehicles.find(v => v.id === vehicleId)?.make + ' ' + vehicles.find(v => v.id === vehicleId)?.model },
        performedBy: user?.id,
      });
      loadVehicles();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Inventory Management</h1>
          <p className="text-muted-foreground">Manage your vehicle listings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/recycle-bin')}>
            <Trash2 className="mr-2 h-4 w-4" />
            Recycle Bin
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setEditingVehicle(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Vehicle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingVehicle ? "Edit Vehicle" : "Add New Vehicle"}</DialogTitle>
            </DialogHeader>

            {editingVehicle ? (
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="details">Vehicle Details</TabsTrigger>
                  <TabsTrigger value="images">Images</TabsTrigger>
                </TabsList>

                <TabsContent value="images" className="space-y-4">
                  <ImageUpload
                    vehicleId={editingVehicle.id}
                    onImagesUploaded={() => loadVehicleImages(editingVehicle.id)}
                  />
                  <VehicleImages
                    vehicleId={editingVehicle.id}
                    images={vehicleImages[editingVehicle.id] || []}
                    onImagesChanged={() => loadVehicleImages(editingVehicle.id)}
                  />
                </TabsContent>

                <TabsContent value="details">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="make">Make *</Label>
                        <Input
                          id="make"
                          required
                          value={formData.make}
                          onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="model">Model *</Label>
                        <Input
                          id="model"
                          required
                          value={formData.model}
                          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="year">Year *</Label>
                        <Input
                          id="year"
                          type="number"
                          required
                          value={formData.year}
                          onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="price">Price *</Label>
                        <Input
                          id="price"
                          type="number"
                          required
                          value={formData.price}
                          onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="mileage">Mileage *</Label>
                        <Input
                          id="mileage"
                          type="number"
                          required
                          value={formData.mileage}
                          onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="color">Exterior Color *</Label>
                        <Input
                          id="color"
                          required
                          value={formData.color}
                          onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="interior_color">Interior Color</Label>
                        <Input
                          id="interior_color"
                          value={formData.interior_color}
                          onChange={(e) => setFormData({ ...formData, interior_color: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="stock_number">Stock Number</Label>
                        <Input
                          id="stock_number"
                          value={formData.stock_number}
                          onChange={(e) => setFormData({ ...formData, stock_number: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="body_type">Body Type</Label>
                        <Input
                          id="body_type"
                          placeholder="e.g. Sedan, SUV"
                          value={formData.body_type}
                          onChange={(e) => setFormData({ ...formData, body_type: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="trim">Trim Level</Label>
                        <Input
                          id="trim"
                          placeholder="e.g. SE, LE"
                          value={formData.trim}
                          onChange={(e) => setFormData({ ...formData, trim: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="vin">VIN *</Label>
                        <Input
                          id="vin"
                          required
                          value={formData.vin}
                          onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="transmission">Transmission *</Label>
                        <Select value={formData.transmission} onValueChange={(value) => setFormData({ ...formData, transmission: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="automatic">Automatic</SelectItem>
                            <SelectItem value="manual">Manual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="fuel_type">Fuel Type *</Label>
                        <Select value={formData.fuel_type} onValueChange={(value) => setFormData({ ...formData, fuel_type: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="gasoline">Gasoline</SelectItem>
                            <SelectItem value="diesel">Diesel</SelectItem>
                            <SelectItem value="electric">Electric</SelectItem>
                            <SelectItem value="hybrid">Hybrid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="drivetrain">Drivetrain *</Label>
                        <Select value={formData.drivetrain} onValueChange={(value) => setFormData({ ...formData, drivetrain: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="fwd">FWD</SelectItem>
                            <SelectItem value="rwd">RWD</SelectItem>
                            <SelectItem value="awd">AWD</SelectItem>
                            <SelectItem value="4wd">4WD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="status">Status *</Label>
                        <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value as "available" | "sold" | "pending" })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="available">Available</SelectItem>
                            <SelectItem value="sold">Sold</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="carfax_url">Carfax URL</Label>
                      <Input
                        id="carfax_url"
                        placeholder="https://www.carfax.ca/..."
                        value={formData.carfax_url}
                        onChange={(e) => setFormData({ ...formData, carfax_url: e.target.value })}
                      />
                    </div>
                    {/* Purchase Information */}
                    <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
                      <h3 className="font-semibold text-sm flex items-center gap-2"><DollarSign className="h-4 w-4" /> Purchase Information</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <Label>Purchase Cost (excl. HST)</Label>
                          <Input type="number" step="0.01" placeholder="0.00"
                            value={formData.purchase_cost}
                            onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })} />
                        </div>
                        <div>
                          <Label>HST Paid on Purchase</Label>
                          <Input type="number" step="0.01" placeholder="0.00"
                            value={formData.purchase_hst}
                            onChange={(e) => setFormData({ ...formData, purchase_hst: e.target.value })} />
                        </div>
                        <div>
                          <Label>Purchase Date</Label>
                          <Input type="date" value={formData.purchase_date}
                            onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} />
                        </div>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={4}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="featured"
                        checked={formData.featured}
                        onCheckedChange={(checked) => setFormData({ ...formData, featured: checked as boolean })}
                      />
                      <Label htmlFor="featured" className="font-normal cursor-pointer">
                        Featured Vehicle (display on homepage)
                      </Label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => {
                        setIsDialogOpen(false);
                        setEditingVehicle(null);
                        resetForm();
                      }}>
                        Cancel
                      </Button>
                      <Button type="submit" disabled={isSaving}>
                        {isSaving ? "Saving..." : (editingVehicle ? "Update" : "Add") + " Vehicle"}
                      </Button>
                    </div>
                  </form>
                </TabsContent>
              </Tabs>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="make">Make *</Label>
                    <Input
                      id="make"
                      required
                      value={formData.make}
                      onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="model">Model *</Label>
                    <Input
                      id="model"
                      required
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="year">Year *</Label>
                    <Input
                      id="year"
                      type="number"
                      required
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="price">Price *</Label>
                    <Input
                      id="price"
                      type="number"
                      required
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="mileage">Mileage *</Label>
                    <Input
                      id="mileage"
                      type="number"
                      required
                      value={formData.mileage}
                      onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="color">Color *</Label>
                    <Input
                      id="color"
                      required
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="interior_color">Interior Color</Label>
                    <Input
                      id="interior_color"
                      value={formData.interior_color}
                      onChange={(e) => setFormData({ ...formData, interior_color: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="stock_number">Stock Number</Label>
                    <Input
                      id="stock_number"
                      value={formData.stock_number}
                      onChange={(e) => setFormData({ ...formData, stock_number: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="body_type">Body Type</Label>
                    <Input
                      id="body_type"
                      placeholder="e.g. Sedan, SUV"
                      value={formData.body_type}
                      onChange={(e) => setFormData({ ...formData, body_type: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="trim">Trim Level</Label>
                    <Input
                      id="trim"
                      placeholder="e.g. SE, LE"
                      value={formData.trim}
                      onChange={(e) => setFormData({ ...formData, trim: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="vin">VIN *</Label>
                    <Input
                      id="vin"
                      required
                      value={formData.vin}
                      onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="transmission">Transmission *</Label>
                    <Select value={formData.transmission} onValueChange={(value) => setFormData({ ...formData, transmission: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="automatic">Automatic</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="fuel_type">Fuel Type *</Label>
                    <Select value={formData.fuel_type} onValueChange={(value) => setFormData({ ...formData, fuel_type: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gasoline">Gasoline</SelectItem>
                        <SelectItem value="diesel">Diesel</SelectItem>
                        <SelectItem value="electric">Electric</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="drivetrain">Drivetrain *</Label>
                    <Select value={formData.drivetrain} onValueChange={(value) => setFormData({ ...formData, drivetrain: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fwd">FWD</SelectItem>
                        <SelectItem value="rwd">RWD</SelectItem>
                        <SelectItem value="awd">AWD</SelectItem>
                        <SelectItem value="4wd">4WD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="status">Status *</Label>
                    <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value as "available" | "sold" | "pending" })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="sold">Sold</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {/* Purchase Information */}
                <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2"><DollarSign className="h-4 w-4" /> Purchase Information</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label>Purchase Cost (excl. HST)</Label>
                      <Input type="number" step="0.01" placeholder="0.00"
                        value={formData.purchase_cost}
                        onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })} />
                    </div>
                    <div>
                      <Label>HST Paid on Purchase</Label>
                      <Input type="number" step="0.01" placeholder="0.00"
                        value={formData.purchase_hst}
                        onChange={(e) => setFormData({ ...formData, purchase_hst: e.target.value })} />
                    </div>
                    <div>
                      <Label>Purchase Date</Label>
                      <Input type="date" value={formData.purchase_date}
                        onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} />
                    </div>
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="featured-add"
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, featured: checked as boolean })}
                  />
                  <Label htmlFor="featured-add" className="font-normal cursor-pointer">
                    Featured Vehicle (display on homepage)
                  </Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => {
                    setIsDialogOpen(false);
                    setEditingVehicle(null);
                    resetForm();
                  }}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? "Adding..." : "Add Vehicle"}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>

    {isLoading ? (
        <div className="text-center py-12">Loading vehicles...</div>
      ) : vehicles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">No vehicles in inventory yet.</p>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Your First Vehicle
                </Button>
              </DialogTrigger>
            </Dialog>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vehicles.map((vehicle) => {
            const images = vehicleImages[vehicle.id] || [];
            const primaryImage = images.find(img => img.is_primary) || images[0];

            return (
              <Card key={vehicle.id} className="overflow-hidden">
                {primaryImage && (
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={primaryImage.image_url}
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className={`w-full h-full object-cover ${vehicle.is_hidden ? 'opacity-60 grayscale' : ''}`}
                      style={(primaryImage as any).object_position !== 'center' ? { objectPosition: (primaryImage as any).object_position } : undefined}
                    />
                    <div className="absolute top-2 right-2 flex gap-1">
                      {vehicle.is_hidden && (
                        <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50 text-xs">
                          Hidden
                        </Badge>
                      )}
                      <Badge variant={vehicle.status === "available" ? "default" : vehicle.status === "sold" ? "secondary" : "outline"}>
                        {vehicle.status}
                      </Badge>
                    </div>
                  </div>
                )}
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-2">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h3>
                  <div className="space-y-1 text-sm text-muted-foreground mb-3">
                    <p>Price: ${vehicle.price.toLocaleString()}</p>
                    <p>Mileage: {vehicle.mileage.toLocaleString()} km</p>
                    <p>VIN: {vehicle.vin}</p>
                    <p>Days on Lot: {Math.floor((Date.now() - new Date(vehicle.created_at).getTime()) / (1000 * 3600 * 24))}</p>
                  </div>
                  {!primaryImage && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                      <ImageIcon className="h-4 w-4" />
                      <span>No images</span>
                    </div>
                  )}
                  <div className="flex gap-2 mb-2">
                    <Button variant="outline" className="flex-1" onClick={() => navigate(`/admin/inventory/${vehicle.id}`)}>
                      View / Edit
                    </Button>
                    <Select value={vehicle.status} onValueChange={(value) => updateVehicleStatus(vehicle.id, value as "available" | "sold" | "pending")}>
                      <SelectTrigger className="flex-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="sold" disabled>Sold (via Deal)</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className={`flex-1 text-xs ${vehicle.is_hidden ? 'border-amber-400 text-amber-700 hover:bg-amber-50' : 'text-muted-foreground'}`}
                      onClick={() => toggleVehicleHidden(vehicle.id, vehicle.is_hidden)}
                    >
                      {vehicle.is_hidden ? (
                        <><Eye className="h-3 w-3 mr-1" /> Show on Website</>
                      ) : (
                        <><EyeOff className="h-3 w-3 mr-1" /> Hide from Site</>
                      )}
                    </Button>
                    <Button variant="secondary" size="sm" className="flex-1 text-xs" onClick={() => generateLeadLink(vehicle.id)}>
                      <LinkIcon className="h-3 w-3 mr-1" />
                      Lead Link
                    </Button>
                  </div>

                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

    <ConfirmDialog
      open={!!deleteTarget}
      onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
      onConfirm={() => deleteTarget && deleteVehicle(deleteTarget.id)}
      title="Delete Vehicle"
      description={deleteTarget ? `Are you sure you want to delete "${deleteTarget.name}"? The vehicle and all related data will be moved to the recycle bin where it can be restored later.` : ""}
      confirmLabel="Move to Recycle Bin"
      variant="destructive"
    />
  </div>
);
}
